import { FC, ReactNode } from 'react';

import ArrowRightUp from '@/assets/icons/ArrowRightUp';
import Navbar from '@/components/navBar/Navbar';

import { Permission } from './permissions';
import { useBamAuth } from './useBamAuth';

type ProtectedRouteProps = {
  /** Single permission required to access the route */
  requires?: Permission;
  /** Access granted if user has ANY of these permissions */
  anyOf?: Permission[];
  /** Access granted if user has ALL of these permissions */
  allOf?: Permission[];
  /** Custom check function for complex logic (e.g., combining permissions + feature flags) */
  check?: (api: {
    hasPermission: (p: Permission) => boolean;
    hasAnyPermission: (ps: Permission[]) => boolean;
    hasAllPermissions: (ps: Permission[]) => boolean;
  }) => boolean;
  children: ReactNode;
};

/**
 * Route-level permission guard.
 * Replaces the pattern: <ProtectedContent isAllowed={(credentials) => ...}>
 *
 * Usage:
 *   <ProtectedRoute requires="agenda.view"><AgendaView /></ProtectedRoute>
 *   <ProtectedRoute anyOf={['beheer.tam', 'beheer.matbeheer']}><BeheerView /></ProtectedRoute>
 */
const ProtectedRoute: FC<ProtectedRouteProps> = ({ requires, anyOf, allOf, check, children }) => {
  const permissionsApi = useBamAuth();

  if (!permissionsApi.isLoggedIn) {
    return (
      <div>
        <Navbar />
        <div
          style={{
            color: 'var(--tractie-color-content-default)',
            display: 'flex',
            justifyContent: 'center',
            marginTop: '160px',
            alignItems: 'center',
          }}
        >
          Log rechtsboven in om te beginnen{' '}
          <div style={{ marginLeft: '12px' }}>
            <ArrowRightUp
              fill='var(--tractie-color-content-emphasis)'
              aria-hidden='true'
              data-testid='icon-ArrowRightUp'
            />
          </div>
        </div>
      </div>
    );
  }

  let allowed = false;

  if (check) {
    allowed = check(permissionsApi);
  } else if (requires) {
    allowed = permissionsApi.hasPermission(requires);
  } else if (anyOf) {
    allowed = permissionsApi.hasAnyPermission(anyOf);
  } else if (allOf) {
    allowed = permissionsApi.hasAllPermissions(allOf);
  }

  if (!allowed) {
    return (
      <div>
        <Navbar />
        <div
          style={{
            color: 'var(--tractie-color-content-default)',
            display: 'flex',
            justifyContent: 'center',
            marginTop: '160px',
          }}
        >
          Het ingelogde account beschikt niet over de juiste autorisatie voor deze applicatie
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
